import { ODDS_SCREEN_TIME_ZONE, getOddsScreenDateKey } from "@/lib/odds-screen/time-formatting";
import type { EventHeader, LiveClock, LivePeriod } from "@/lib/odds-screen/live/types";

const clockFormatter = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: ODDS_SCREEN_TIME_ZONE,
});

const weekdayFormatter = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: ODDS_SCREEN_TIME_ZONE });

const metaFormatter = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  timeZone: ODDS_SCREEN_TIME_ZONE,
});

const kickoffDateFormatter = new Intl.DateTimeFormat("en-US", {
  weekday: "long",
  month: "short",
  day: "numeric",
  timeZone: ODDS_SCREEN_TIME_ZONE,
});

const daySegWeekday = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: "UTC" });
const daySegDay = new Intl.DateTimeFormat("en-US", { day: "numeric", timeZone: "UTC" });

export function daySeg(dateKey: string): string {
  const [year, month, day] = dateKey.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return `${daySegWeekday.format(date)} ${daySegDay.format(date)}`;
}

export const KICKOFF_TIME = "data-num whitespace-nowrap text-[12px] font-semibold text-primary";

export const LIVE_START = -1;

export function groupStart(event: Pick<EventHeader, "startMs" | "live"> | undefined): number | undefined {
  return event?.live ? LIVE_START : event?.startMs;
}

const ORDINAL_SUFFIX = ["th", "st", "nd", "rd"];

function ordinal(value: number): string {
  const teen = value % 100 >= 11 && value % 100 <= 13;
  return `${value}${teen ? "th" : ORDINAL_SUFFIX[value % 10] ?? "th"}`;
}

const UNIT_NAMES: Record<"quarter" | "half" | "period" | "set", readonly [string, (n: number) => string]> = {
  quarter: ["Quarter", (n) => `Q${n}`],
  half: ["Half", (n) => `${n}H`],
  period: ["Period", (n) => `P${n}`],
  set: ["Set", (n) => `S${n}`],
};

export function periodLabel(period: LivePeriod, compact = false): string {
  const { unit, number: n, half } = period;
  switch (unit) {
    case "inning":
      if (half === null) return compact ? `Inn ${n}` : `${ordinal(n)} Inning`;
      if (period.break) return `${half === "top" ? "Mid" : "End"} ${ordinal(n)}`;
      return `${half === "top" ? "Top" : compact ? "Bot" : "Bottom"} ${ordinal(n)}`;
    case "round":
      return compact ? `R${n}` : `Round ${n}`;
    case "overtime":
      return `${period.break ? "End " : ""}${n > 1 ? n : ""}OT`;
    case "extra_time":
      return compact ? `ET${n}` : `ET ${ordinal(n)} Half`;
    case "shootout":
      return compact ? "SO" : "Shootout";
  }
  if (period.break && ((unit === "quarter" && n === 2) || (unit === "half" && n === 1))) return compact ? "HT" : "Halftime";
  const [name, short] = UNIT_NAMES[unit];
  if (period.break) return compact ? `End ${short(n)}` : `End of ${ordinal(n)}`;
  return compact ? short(n) : `${ordinal(n)} ${name}`;
}

export function clockSeconds(clock: LiveClock, nowMs: number): number {
  if (!clock.running) return clock.seconds;
  return Math.max(0, clock.seconds - Math.floor(Math.max(0, nowMs - clock.atMs) / 1000));
}

export function clockText(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export function startedAt(startMs: number, now: Date = new Date()): string {
  return `${dayClock(startMs, now)} ET`;
}

export function clock(startMs: number): string {
  return `${clockFormatter.format(new Date(startMs))} ET`;
}

export function dayClock(startMs: number, now: Date = new Date()): string {
  const start = new Date(startMs);
  const time = clockFormatter.format(start);
  return getOddsScreenDateKey(start) === getOddsScreenDateKey(now) ? time : `${weekdayFormatter.format(start)} ${time}`;
}

export function kickoffGroups(starts: readonly number[]): { counts: number[]; starts: number[] } {
  const groups = { counts: [] as number[], starts: [] as number[] };
  for (const startMs of starts) {
    const last = groups.starts.length - 1;
    if (last >= 0 && groups.starts[last] === startMs) groups.counts[last] += 1;
    else { groups.starts.push(startMs); groups.counts.push(1); }
  }
  return groups;
}

export function kickoffKeys(groups: { counts: readonly number[]; starts: readonly number[] }, itemKeys: readonly string[]): string[] {
  const keys: string[] = [];
  let offset = 0;
  groups.counts.forEach((count, group) => {
    keys.push(`group:${group}:${groups.starts[group]}`);
    keys.push(...itemKeys.slice(offset, offset + count));
    offset += count;
  });
  return keys;
}

export function metaDate(startMs: number): string {
  return metaFormatter.format(new Date(startMs));
}

export function kickoffParts(startMs: number): { date: string; time: string } {
  return { date: kickoffDateFormatter.format(new Date(startMs)), time: clock(startMs) };
}

export function metaLine(startMs: number): string {
  return `${metaDate(startMs)} · ${clock(startMs)}`;
}
